import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Image } from 'antd';
import messages from 'containers/OrdersPage/messages';
import useProductUomInfo from 'hooks/useProductUomInfo';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import generateProductSlug from 'utils/generateProductSlug';
import utilsMessages from 'utils/messages';
import { FulfillmentNote } from '../../types/schema';
import { getUomNameLocal } from '../../utils/getUomNameLocal';
import Money from '../Money/Money';
import ItemQuantity from '../Order/ItemQuantity';
import noImage from './noIMG.svg';

const ProductContainer = styled.div`
  padding: 16px;
  border-bottom: 1px solid #eaeaeb;
`;

const ProductHeader = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 16px;
`;

const ProductWrapper = styled.div`
  display: flex;
  margin-bottom: 20px;
`;

const ProductImageWrapper = styled.div`
  width: 56px;
  height: 56px;
  margin-right: 16px;
  flex-shrink: 0;
`;

const ProductImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ProductInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  & > a {
    color: #585a60;
  }
`;
const ProductMetaWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;
const ProductMeta = styled.span`
  font-size: 12px;
  color: #808289;
`;

const ProductMetaValue = styled(ProductMeta)`
  color: #585a60;
`;

const ProductPrice = styled.div`
  display: flex;
  flex-direction: column;
`;
const ProductPriceValue = styled.p`
  font-color: #585a60;
  font-size: 12px;
  font-weight: 500;
`;
const VatInfo = styled.span`
  color: #808289;
  font-size: 10px;
  font-weight: 500;
`;

const ShowMoreButton = styled.div`
  text-align: center;
  color: #1e7afd;
  padding: 12px 0;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface FFMNoteMobileTableProps {
  fulfillmentNote: FulfillmentNote;
}

interface ProductItemProps {
  item: any;
  lang: string;
}

const FFMNoteMobileTable: FC<FFMNoteMobileTableProps> = (props) => {
  const { fulfillmentNote } = props;
  const [showAll, setShowAll] = useState(false);
  const lang = localStorage.getItem('lang') || 'vi';

  const visibleItems = showAll ? fulfillmentNote.items : fulfillmentNote.items?.slice(0, 3) || [];
  const itemCount = fulfillmentNote.itemCount || 0;

  const handleToggleShowMore = () => {
    setShowAll(!showAll);
  };

  return (
    <div>
      <ProductContainer>
        <ProductHeader>
          <FormattedMessage {...messages.product} /> ({itemCount})
        </ProductHeader>

        {visibleItems.map((item) => (
          <ProductItem key={item.product?.id} item={item} lang={lang} />
        ))}

        {itemCount > 3 && (
          <ShowMoreButton onClick={handleToggleShowMore}>
            {showAll ? (
              <>
                <FormattedMessage {...messages.hideLess} />
                <UpOutlined style={{ fontSize: '12px', marginLeft: '5px' }} />
              </>
            ) : (
              <>
                <FormattedMessage {...utilsMessages.seeAll} />
                <DownOutlined style={{ fontSize: '12px', marginLeft: '5px' }} />
              </>
            )}
          </ShowMoreButton>
        )}
      </ProductContainer>
    </div>
  );
};

const ProductItem: FC<ProductItemProps> = ({ item, lang }) => {
  const { product, orderedQuantity, deliveryQuantity, total } = item;
  const name = product?.name || '';
  const vat = product?.vat || 0;
  const supplierInternalCode = product?.supplierInternalCode || '';
  const imageUrl = product?.imageUrl;

  const uomName = getUomNameLocal(product?.uomLocal || '');
  const productSlug = generateProductSlug(name, supplierInternalCode, lang);
  const totalValue = typeof total === 'string' ? parseFloat(total) : total || 0;

  const { uomInfo } = useProductUomInfo(product);

  return (
    <ProductWrapper>
      <ProductImageWrapper>
        <ProductImage src={imageUrl || noImage} alt={name} preview={false} />
      </ProductImageWrapper>
      <ProductInfo>
        <ProductName>
          <Link to={`/products/${productSlug}`}>{name}</Link>
        </ProductName>
        <ProductMeta>{uomInfo}</ProductMeta>
        <ProductMetaWrapper>
          <div>
            <ProductMeta>
              <FormattedMessage {...utilsMessages.quantity} />:{' '}
            </ProductMeta>
            <ProductMetaValue>
              <ItemQuantity
                orderedQuantity={Number(orderedQuantity)}
                deliveryQuantity={Number(deliveryQuantity)}
                uom={uomName}
              />
            </ProductMetaValue>
          </div>

          <ProductPrice>
            <ProductPriceValue>
              <Money>{totalValue}</Money>
            </ProductPriceValue>
            <VatInfo> ({vat}% VAT)</VatInfo>
          </ProductPrice>
        </ProductMetaWrapper>
      </ProductInfo>
    </ProductWrapper>
  );
};

export default FFMNoteMobileTable;
